import React from "react";

export type DisclosureCallbacks = {
  onOpen?: () => void;
  onClose?: () => void;
  onChange?: (v: boolean) => void;
};

export const useDisclosure = (
  defaultValue?: boolean,
  callbacks?: DisclosureCallbacks,
) => {
  const [state, _set] = React.useState(defaultValue ?? false);
  const callbacksRef = React.useRef<DisclosureCallbacks | undefined>(callbacks);
  React.useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);
  const set = React.useCallback<React.Dispatch<React.SetStateAction<boolean>>>(
    (a) =>
      _set((s) => {
        const next = typeof a == "function" ? a(s) : a;
        const cb = callbacksRef.current;
        if (s && !next) {
          if (cb?.onChange) cb?.onChange(next);
          if (cb?.onClose) cb?.onClose();
        } else if (!s && next) {
          if (cb?.onChange) cb?.onChange(next);
          if (cb?.onOpen) cb?.onOpen();
        }
        return next;
      }),
    [_set],
  );
  return [
    state,
    React.useMemo(
      () => ({
        set,
        open: () => set(true),
        close: () => set(false),
        toggle: () => set((s) => !s),
      }),
      [set],
    ),
  ] as const;
};
